import { getUserId } from "shared-frontend/functions/auth";

import { useGetLegacyPricingNotice } from "../api/generated/customer/customer";

/**
 * The notice as it reads before the query resolves, on error, and when there is no customer to ask
 * about.
 *
 * Frozen and shared so the identity is stable across renders: `isEligible: false` means the banner
 * returns null, and a fresh object each time would give it a new prop on every render for nothing.
 *
 * Erring towards *not* showing the banner is deliberate. It is the only safe default: a notice shown
 * to a customer it does not apply to tells them their renewal price is changing when it is not.
 */
const NO_NOTICE = Object.freeze( { isEligible: false, affectedPlans: Object.freeze( [] ), newPrice: null } );

/**
 * What the notice states the affected plans will renew at.
 *
 * @typedef {object} LegacyPricingNewPrice
 * @property {number} amount      The amount in minor units, already multiplied by the site limit.
 * @property {string} currency    ISO 4217 code of the customer's currency.
 * @property {string} billingTerm Either "year" or "month", for the wording of the period.
 */

/**
 * How long the answer is treated as fresh.
 *
 * Cohort membership changes only when a migration job runs, so fifteen minutes is far tighter than the
 * thirty-day window this serves and still collapses a session's worth of navigations into one request.
 */
const NOTICE_FRESH_FOR_MS = 1000 * 60 * 15;

/**
 * Whether the pricing-migration notice applies to the logged-in customer, and which plans it names.
 *
 * From `GET Customers/:id/subscriptions/legacy-pricing-notice` — Yoast/Platform#4576. The cohort is
 * decided entirely server-side: the customer has an un-switched migration row whose notification mail
 * was never confirmed sent, and they still hold a live subscription on one of the affected SKUs.
 * Neither half is knowable in the browser, which is why this is a read model rather than a selector.
 *
 * Deliberately holds no `useEffect`: this is a read, so there is nothing to mirror into Redux and
 * nothing to report to the data layer. Analytics belong on the mutation that caused an event, never on
 * a query result, which reflects cache state rather than something having happened.
 *
 * `newPrice` is null far more often than not: it is stated only for the one affected SKU the migration
 * actually replaces with another product, and only when the customer's affected plans agree on a single
 * figure. The banner renders a sentence without a price for the rest, so consumers branch on it rather
 * than treating null as a failure.
 *
 * @param {boolean} [isEnabled] Whether to ask at all. Pass the banner's window check: while the notice
 *                               cannot be shown there is nothing to ask about, and the caller is mounted
 *                               on every logged-in page.
 *
 * @returns {{notice: {isEligible: boolean, affectedPlans: string[], newPrice: ?LegacyPricingNewPrice},
 *            isLoading: boolean}} The notice and query state.
 */
export default function useLegacyPricingNotice( isEnabled = true ) {
	const customerId = getUserId();

	const { data, isLoading } = useGetLegacyPricingNotice( customerId, {
		query: {
			// `getUserId()` returns an empty string when there is no access token, and the generated
			// `enabled` only guards null and undefined — so without this the hook would request
			// `Customers//subscriptions/legacy-pricing-notice`.
			//
			// `isEnabled` is the caller's window check. The banner is mounted in the main layout, so
			// without it every logged-in page load would ask a question whose answer is discarded — and
			// with `staleTime: 0` and `refetchOnWindowFocus` app-wide, once per navigation and per tab
			// focus at that, for the whole customer base, for a notice that is off.
			enabled: isEnabled && Boolean( customerId ),
			// The one place a `staleTime` is right despite `queryClient.ts` arguing for 0. That reasoning
			// is about data another page can change; this answer is written only by the migration jobs, so
			// nothing the customer does in this app can invalidate it. Revalidating per navigation would
			// buy nothing and cost a query per page view.
			staleTime: NOTICE_FRESH_FOR_MS,
		},
	} );

	return {
		notice: data ?? NO_NOTICE,
		isLoading,
	};
}
